/**
 * Combine Paper Templates.
 *
 * UX: User selects a Product, then chooses any 3 templates mapped to that product.
 * On submit, confirm total questions (Yes/No).
 */
import React, { useMemo, useState } from 'react'
import { Form as AntForm } from 'antd'
import {
  Button,
  Form,
  Input,
  Modal,
  ModalProps,
  Select,
  Spin,
  Typography,
  useSelect,
  message,
} from '@pankod/refine-antd'
import { useCreate, useCreateMany, useDataProvider, useGetIdentity, useList, useOne } from '@pankod/refine-core'
import { MergeCellsOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons'
import { IPaperTemplate, IPaperTemplateSubSection, IProduct } from 'interfaces'
import paperTemplateMetaData from 'metadata/paperTemplate'
import paperTemplateSubSectionsMeta from 'metadata/paperTemplateSubSections'
import { checkMarkWithSectionTotal } from 'helpers/paperTemplate/checkingTotalMarks'
import { getLastCreatedPaperTemplate } from 'gql/mutations'

function coerceQuestionCount(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function sumTemplateQuestions(template: IPaperTemplate | undefined): number {
  const rows = template?.paper_template
  if (!rows?.length) return 0
  return rows.reduce((acc, row) => acc + coerceQuestionCount(row?.no_of_questions), 0)
}

/** Tolerate Refine / GraphQL / nested provider shapes when reading create mutation results. */
function extractCreatedIdFromResponse(response: unknown): string | undefined {
  if (response == null) return undefined
  if (typeof response !== 'object') return undefined
  const r = response as Record<string, unknown>
  if (typeof r.id === 'string') return r.id
  const data = r.data
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>
    if (typeof d.id === 'string') return d.id
    const inner = d.data
    if (inner && typeof inner === 'object' && typeof (inner as Record<string, unknown>).id === 'string') {
      return (inner as Record<string, unknown>).id as string
    }
  }
  return undefined
}

function sortSubsectionsStable(rows: IPaperTemplateSubSection[]): IPaperTemplateSubSection[] {
  return [...rows].sort((a, b) => {
    const an = String(a?.name ?? '')
    const bn = String(b?.name ?? '')
    if (an !== bn) return an.localeCompare(bn)
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''))
  })
}

function mapSubsectionToInsert(row: Record<string, unknown>, paperTemplateId: string) {
  const difficulty =
    row.difficult_level_id != null
      ? row.difficult_level_id
      : row.difficulty_level_id != null
      ? row.difficulty_level_id
      : null

  return {
    paper_template_id: paperTemplateId,
    name: row.name,
    section_id: row.section_id,
    subject_id: row.subject_id,
    theme_id: row.theme_id ?? null,
    sub_theme_id: row.sub_theme_id ?? null,
    topic_id: row.topic_id ?? null,
    // Note: backend GraphQL schema treats these as text in some environments
    // (other flows pass them through as strings). Keep numeric math elsewhere.
    no_of_questions: String(coerceQuestionCount(row.no_of_questions)),
    mark_per_question: String(coerceQuestionCount(row.mark_per_question)),
    question_type_id: row.question_type_id,
    report_theme_id: row.report_theme_id ?? null,
    blooms_level_id: row.blooms_level_id ?? null,
    difficult_level_id: difficulty,
    competency_id: row.competency_id ?? null,
    strand_id: row.strand_id ?? null,
    base_lang: row.base_lang ?? null,
    target_lang: row.target_lang ?? null,
  }
}

function describeError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  if (err && typeof err === 'object') {
    const e = err as any
    const direct = typeof e?.message === 'string' ? e.message : undefined
    const respMsg =
      typeof e?.response?.data?.message === 'string'
        ? e.response.data.message
        : typeof e?.response?.data?.error === 'string'
        ? e.response.data.error
        : undefined
    const gqlMsg =
      Array.isArray(e?.errors) && e.errors[0] && typeof e.errors[0].message === 'string' ? e.errors[0].message : undefined
    return direct || respMsg || gqlMsg || 'Failed to combine paper templates.'
  }
  return 'Failed to combine paper templates.'
}

export type CombinePaperTemplatesModalProps = {
  open: boolean
  onClose: () => void
  onSuccess: () => void | Promise<void>
}

export const CombinePaperTemplatesModal: React.FC<CombinePaperTemplatesModalProps> = ({ open, onClose, onSuccess }) => {
  const [form] = Form.useForm()
  const { data: user } = useGetIdentity()
  const [submitting, setSubmitting] = useState(false)

  const selectedProductId = AntForm.useWatch('product_id', form)
  const tqId = AntForm.useWatch('tq_paper_template_id', form)
  const sktId = AntForm.useWatch('skt_paper_template_id', form)
  const otherId = AntForm.useWatch('other_paper_template_id', form)
  const additionalIds = (AntForm.useWatch('additional_paper_template_ids', form) as (string | undefined)[]) ?? []

  const listMeta = useMemo(
    () => ({
      fields: [
        'id',
        'name',
        'description',
        'product_id',
        'test_track_id',
        'sections',
        { product: ['id', 'name', 'type'] },
        { test_tracks: ['id', 'name'] },
        { paper_template: ['id', 'no_of_questions', 'name'] },
      ],
    }),
    []
  )

  const { data: listResult, isLoading: listLoading } = useList<IPaperTemplate>({
    resource: 'paper_templates',
    config: {
      hasPagination: false,
      filters: [
        {
          field: 'active',
          operator: 'eq',
          value: 'true',
        },
      ],
    },
    metaData: listMeta,
    queryOptions: { enabled: open, staleTime: 30_000 },
  })

  const templates = listResult?.data ?? []

  const { selectProps: productSelectProps } = useSelect<IProduct>({
    resource: 'products',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 1000,
    metaData: { fields: ['id', 'name', 'type'] },
    sort: [{ field: 'name', order: 'asc' }],
    // Note: backend expects string for boolean filters (matches existing create flow)
    filters: [{ field: 'active', operator: 'eq', value: 'true' }],
    queryOptions: { enabled: open },
  })

  const { data: productOne } = useOne<IProduct>({
    resource: 'products',
    id: selectedProductId ?? '',
    metaData: {
      fields: ['id', 'sections', 'total_marks', 'total_questions'],
    },
    queryOptions: { enabled: open && !!selectedProductId },
  })

  const productData = productOne?.data

  const { mutateAsync: createTemplate } = useCreate<IPaperTemplate>()
  const { mutateAsync: createSubsections } = useCreateMany()
  const dataProvider = useDataProvider()

  const templatesForProduct = useMemo(() => {
    if (!selectedProductId) return [] as IPaperTemplate[]
    return templates.filter(t => t.product_id === selectedProductId)
  }, [templates, selectedProductId])

  const selectedSet = useMemo(() => {
    const all = [tqId, sktId, otherId, ...additionalIds].filter(Boolean) as string[]
    return new Set(all)
  }, [tqId, sktId, otherId, additionalIds])

  const makeOptions = (currentValue: string | undefined, sourceList: IPaperTemplate[], lockSelection: boolean) => {
    return sourceList.map(t => {
      const n = sumTemplateQuestions(t)
      const productType = t.product?.type ? String(t.product.type) : '—'
      const label = `${t.name} [${productType}] — ${n} Q`
      const disabled = lockSelection || (t.id !== currentValue && selectedSet.has(t.id))
      return { value: t.id, label, disabled }
    })
  }

  const selectsLocked = !selectedProductId

  const tqOptions = makeOptions(tqId as string | undefined, templatesForProduct, selectsLocked)
  const sktOptions = makeOptions(sktId as string | undefined, templatesForProduct, selectsLocked)
  const otherOptions = makeOptions(otherId as string | undefined, templatesForProduct, selectsLocked)

  const resetForm = () => {
    form.resetFields()
  }

  const modalProps: ModalProps = {
    visible: open,
    title: (
      <span>
        <MergeCellsOutlined style={{ marginRight: 8 }} />
        Combine paper templates
      </span>
    ),
    onCancel: () => {
      resetForm()
      onClose()
    },
    footer: null,
    destroyOnClose: true,
    width: 640,
  }

  const onSubmit = async () => {
    try {
      await form.validateFields()
    } catch {
      return
    }

    const values = form.getFieldsValue()
    const name = values.name as string
    const description = (values.description as string) || ''
    const tq = values.tq_paper_template_id as string | undefined
    const skt = values.skt_paper_template_id as string | undefined
    const other = values.other_paper_template_id as string | undefined
    const additional = ((values.additional_paper_template_ids as (string | undefined)[]) ?? []).filter(Boolean) as string[]

    const orderedIds = [tq, skt, other, ...additional].filter(Boolean) as string[]
    if (orderedIds.length < 2) {
      message.error('Select at least two source templates.')
      return
    }

    const allIds = orderedIds
    if (new Set(allIds).size !== allIds.length) {
      message.error('All selected source templates must be different.')
      return
    }

    const tm = (id: string) => templates.find(x => x.id === id)
    const selectedTemplates = allIds.map(id => tm(id))

    if (selectedTemplates.some(x => !x)) {
      message.error('Could not load template details. Refresh and try again.')
      return
    }

    const selectedTemplatesSafe = selectedTemplates as IPaperTemplate[]
    const totalCount = selectedTemplatesSafe.reduce((acc, t) => acc + sumTemplateQuestions(t), 0)

    const confirmed = await new Promise<boolean>(resolve => {
      Modal.confirm({
        title: 'Confirm',
        content: <p>This template has {totalCount} questions. Is that ok?</p>,
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })

    if (!confirmed) return

    const firstTemplate = selectedTemplatesSafe[0]
    if (selectedTemplatesSafe.some(t => t.product_id !== firstTemplate.product_id)) {
      message.error('All source templates must use the same product.')
      return
    }

    setSubmitting(true)
    try {
      const dp = dataProvider()
      const subList = await dp.getList<IPaperTemplateSubSection>({
        resource: 'paper_template_sub_sections',
        hasPagination: false,
        filters: [
          {
            field: 'paper_template_id',
            operator: 'in',
            value: allIds,
          },
        ],
        metaData: paperTemplateSubSectionsMeta,
      })

      const byTemplate: Record<string, IPaperTemplateSubSection[]> = {}
      for (const id of allIds) byTemplate[id] = []
      for (const row of subList.data ?? []) {
        const pid = row.paper_template_id
        if (byTemplate[pid]) byTemplate[pid].push(row)
      }

      const merged = orderedIds.flatMap(id => sortSubsectionsStable(byTemplate[id] ?? []))
      if (merged.length === 0) {
        message.error('No subsections found for the selected templates. Please check the source templates and try again.')
        return
      }

      const sectionIdsOrdered: string[] = []
      for (const row of merged) {
        const sid = row.section_id as string
        if (sid && !sectionIdsOrdered.includes(sid)) sectionIdsOrdered.push(sid)
      }

      const forCompletionCheck = merged.map(r => ({
        section_id: r.section_id,
        no_of_questions: coerceQuestionCount(r.no_of_questions),
        mark_per_question: coerceQuestionCount(r.mark_per_question),
      }))

      let is_completed = false
      if (productData) {
        const chk = await checkMarkWithSectionTotal([productData] as any[], forCompletionCheck as any[])
        is_completed = chk.status === true && (chk as { flag?: number }).flag === 1
      }

      const createRes = await createTemplate({
        resource: 'paper_templates',
        values: {
          name,
          description,
          product_id: firstTemplate.product_id,
          test_track_id: firstTemplate.test_track_id,
          status: '',
          sections: sectionIdsOrdered,
          created_by: user?.id,
          active: true,
          is_completed,
        },
        metaData: paperTemplateMetaData,
        successNotification: false,
      })

      let newId = extractCreatedIdFromResponse(createRes)
      if (!newId) {
        const fallback = (await getLastCreatedPaperTemplate()) as { paper_templates?: { id: string }[] }
        newId = fallback?.paper_templates?.[0]?.id
      }
      if (!newId) {
        message.error('Paper template was created but the new id could not be read. Check the list and add subsections manually if needed.')
        await onSuccess()
        resetForm()
        onClose()
        return
      }

      const paperTemplateId: string = newId
      const insertRows = merged.map(row => mapSubsectionToInsert(row as unknown as Record<string, unknown>, paperTemplateId))

      // Batch insert to avoid backend payload/row limits.
      const BATCH_SIZE = 200
      for (let i = 0; i < insertRows.length; i += BATCH_SIZE) {
        const batch = insertRows.slice(i, i + BATCH_SIZE)
        await createSubsections({
          resource: 'paper_template_sub_sections',
          values: batch,
          metaData: paperTemplateSubSectionsMeta,
          successNotification: false,
        })
      }

      message.success(`Combined paper template created (${totalCount} questions).`)
      await onSuccess()
      resetForm()
      onClose()
    } catch (e: unknown) {
      console.error(e)
      message.error(describeError(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal {...modalProps}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Select a Product, then pick any two or more templates (different IDs) mapped to that product. Subsections are stored in order:
        TQ → SKT → Other (then any additional templates you add).
      </Typography.Paragraph>

      <Spin spinning={listLoading || submitting}>
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item label="New template name" name="name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="Name" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea placeholder="Optional" rows={2} />
          </Form.Item>

          <Form.Item label="Product" name="product_id" rules={[{ required: true, message: 'Select a product' }]}>
            <Select
              {...productSelectProps}
              placeholder="Select product"
              onChange={() => {
                form.setFieldsValue({
                  tq_paper_template_id: undefined,
                  skt_paper_template_id: undefined,
                  other_paper_template_id: undefined,
                  additional_paper_template_ids: [],
                })
              }}
            />
          </Form.Item>

          <Form.Item
            label="TQ source"
            name="tq_paper_template_id"
          >
            <Select
              placeholder="Select template"
              options={tqOptions}
              showSearch
              optionFilterProp="label"
              loading={listLoading}
              disabled={selectsLocked}
            />
          </Form.Item>

          <Form.Item
            label="SKT source"
            name="skt_paper_template_id"
            extra={
              <>
                {selectsLocked ? <Typography.Text type="secondary">Select a Product first.</Typography.Text> : null}
              </>
            }
          >
            <Select
              placeholder="Select template"
              options={sktOptions}
              showSearch
              optionFilterProp="label"
              loading={listLoading}
              disabled={selectsLocked}
            />
          </Form.Item>

          <Form.Item label="Other source" name="other_paper_template_id">
            <Select
              placeholder="Select template"
              options={otherOptions}
              showSearch
              optionFilterProp="label"
              loading={listLoading}
              disabled={selectsLocked}
            />
          </Form.Item>

          <AntForm.List name="additional_paper_template_ids">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, idx) => {
                  const current = additionalIds?.[idx]
                  const options = makeOptions(current, templatesForProduct, selectsLocked)
                  return (
                    <Form.Item
                      key={field.key}
                      label={`Additional template ${idx + 1}`}
                      required={false}
                      style={{ marginBottom: 12 }}
                    >
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Form.Item {...field} noStyle>
                          <Select
                            placeholder="Select template"
                            options={options}
                            showSearch
                            optionFilterProp="label"
                            loading={listLoading}
                            disabled={selectsLocked}
                            style={{ flex: 1 }}
                          />
                        </Form.Item>
                        <Button
                          onClick={() => remove(field.name)}
                          danger
                          type="text"
                          aria-label="Remove additional template"
                          icon={<MinusCircleOutlined />}
                        />
                      </div>
                    </Form.Item>
                  )
                })}

                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add(undefined)}
                    block
                    icon={<PlusOutlined />}
                    disabled={selectsLocked}
                  >
                    Add another template
                  </Button>
                </Form.Item>
              </>
            )}
          </AntForm.List>

          <Button type="primary" htmlType="submit" loading={submitting} block icon={<MergeCellsOutlined />}>
            Create combined template
          </Button>
        </Form>
      </Spin>
    </Modal>
  )
}
